import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { Badge, Card, CardHeader, Detail, EmptyState, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { KpiCard, PeriodLine, useNumber } from '../components/kit';
import { AXIS, ChartFrame, GRID, SERIES, TableTwin, tooltipItemStyle, tooltipLabelStyle, tooltipStyle } from '../components/b-charts';
import { BackLink, Loaded, Note, PersonName, PriorityBadge, StatusBadge, TextLink, sourceHref, useDates } from '../components/b-shared';
import { MoneyCell, ProgressText, RiskBadge, type ProjectRow, type WithPeriod } from '../components/b-projects';

/**
 * One project (CR-008 §9.2): its row's figures, then what needs a look — overdue and blocked tasks, open
 * tickets — its contracts, and the tracked tasks it completed week by week. No margin: no cost is attributed
 * to a project.
 */

type Person = { id: string; fullName: string; fullNameAr: string | null } | null;

interface ProjectDetail extends ProjectRow, WithPeriod {
  overdueTasks: Array<{ id: string; title: string; status: string; priority: string; dueAt: string | null; assignee: Person; department: { id: string; name: string; nameAr: string | null } | null }>;
  blockedTasks: Array<{ id: string; title: string; priority: string; dueAt: string | null; updatedAt: string; assignee: Person }>;
  openTickets: Array<{ id: string; number: string; subject: string; status: string; priority: string; createdAt: string; assignee: Person }>;
  contracts: Array<{ id: string; number: string; title: string; status: string; startDate: string; endDate: string; billing: string; currency: string }>;
  completedByWeek: Array<{ week: string; done: number }>;
  margin: { available: boolean; reason: string };
}

const LIST_CAP = 25;

export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const { params, query } = usePeriod();
  const detail = useQuery({
    queryKey: ['executive', 'project', id, params],
    queryFn: () => api.get<ProjectDetail>(`/executive/projects/${id}`, { ...params }),
    enabled: !!id,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === id ? previous : undefined),
  });
  return (
    <>
      <BackLink to={`/projects${query ? `?${query}` : ''}`}>{t('projects.back')}</BackLink>
      <Loaded query={detail}>
        {(data) =>
          data.project ? (
            <ProjectView data={data} />
          ) : (
            <Card>
              <EmptyState title={t('common.noResults')} hint={t('projects.beyondList')} />
            </Card>
          )
        }
      </Loaded>
    </>
  );
}

function ProjectView({ data }: { data: ProjectDetail }) {
  const { t, localized, locale } = useI18n();
  const { query } = usePeriod();
  const num = useNumber();
  const dates = useDates();
  const search = query ? `?${query}` : '';
  const tz = data.period.timezone;
  return (
    <>
      <PageHeader
        title={
          <span>
            {localized(data.project)}
            <span dir="ltr" className="ms-2 font-mono text-body text-[var(--text-tertiary)]">
              {data.project.code}
            </span>
          </span>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            {data.client ? <TextLink to={`/clients/${data.client.id}${search}`}>{localized(data.client)}</TextLink> : <Badge tone="neutral">{t('common.internal')}</Badge>}
            {data.owner && (
              <span>
                · {t('projects.owner')}: <PersonName person={data.owner} />
              </span>
            )}
          </span>
        }
      />
      <div className="space-y-5">
        <PeriodLine period={data.period} />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card>
            <p className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('projects.col.risk')}</p>
            <div className="mt-2 space-y-2">
              <StatusBadge status={data.project.status} />
              <RiskBadge risk={data.risk} />
            </div>
          </Card>
          <KpiCard
            label={t('projects.col.progress')}
            value={data.progress.tracked ? <span dir="ltr">{`${num(data.progress.done)} / ${num(data.progress.tracked)}`}</span> : '—'}
            hint={<ProgressText progress={data.progress} />}
          />
          <KpiCard
            label={t('projects.detail.overdueTasks')}
            value={num(data.tasks.overdue)}
            tone={data.tasks.overdue > 0 ? 'error' : 'neutral'}
            hint={t('projects.detail.openBlocked', { open: num(data.tasks.open), blocked: num(data.tasks.blocked), review: num(data.tasks.review) })}
          />
          <KpiCard label={t('projects.col.openTickets')} value={num(data.tickets.open)} hint={t('projects.detail.resolvedInPeriod', { n: num(data.tickets.resolvedInPeriod) })} />
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          <Card>
            <CardHeader title={t('projects.detail.money')} subtitle={t('projects.moneyNote')} />
            <dl>
              <Detail label={t('projects.col.invoiced')}>
                <MoneyCell money={data.money} field="invoiced" />
              </Detail>
              <Detail label={t('projects.col.collected')}>
                <MoneyCell money={data.money} field="collected" />
              </Detail>
              <Detail label={t('projects.col.outstanding')}>
                <MoneyCell money={data.money} field="outstanding" />
              </Detail>
              <Detail label={t('projects.detail.margin')}>
                <span className="text-muted">{t('state.unavailable')}</span>
              </Detail>
            </dl>
            <Note className="mt-3">{t('projects.marginNote')}</Note>
          </Card>
          <Card>
            <CardHeader title={t('projects.detail.dates')} />
            <dl>
              <Detail label={t('projects.detail.start')}>{dates.day(data.project.startDate)}</Detail>
              <Detail label={t('projects.detail.expectedEnd')}>{dates.day(data.project.expectedEndDate)}</Detail>
              <Detail label={t('projects.detail.actualEnd')}>{dates.day(data.project.actualEndDate)}</Detail>
              <Detail label={t('common.priority')}>
                <PriorityBadge priority={data.project.priority} />
              </Detail>
            </dl>
          </Card>
          <Card>
            <CardHeader title={t('projects.col.support')} />
            {data.support ? (
              <dl>
                <Detail label={t('projects.detail.contract')}>
                  <span dir="ltr" className="font-mono text-caption">
                    {data.support.number}
                  </span>
                </Detail>
                <Detail label={t('projects.detail.until')}>{dates.day(data.support.until)}</Detail>
              </dl>
            ) : (
              <p className="text-body-sm text-muted">{t('projects.noSupport')}</p>
            )}
          </Card>
        </div>

        <WeeklyChart data={data} />

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('projects.detail.overdueList')} subtitle={t('projects.detail.overdueListHint')} />
          </div>
          {data.overdueTasks.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('projects.detail.noOverdue')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('projects.detail.task')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th>{t('evidence.col.deadline')}</Th>
                  <Th>{t('evidence.item.assignee')}</Th>
                  <Th>{t('common.department')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.overdueTasks.map((task) => (
                  <Tr key={task.id}>
                    <Td className="min-w-[14rem]">
                      <TextLink to={sourceHref('TASK', task.id, query) ?? '#'}>{task.title}</TextLink>
                    </Td>
                    <Td>
                      <StatusBadge status={task.status} />
                    </Td>
                    <Td>
                      <PriorityBadge priority={task.priority} />
                    </Td>
                    <Td className="whitespace-nowrap text-error-fg dark:text-error-dark">{dates.at(task.dueAt, tz)}</Td>
                    <Td>
                      <PersonName person={task.assignee} />
                    </Td>
                    <Td className="text-[var(--text-secondary)]">{task.department ? localized(task.department) : '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
          {data.overdueTasks.length >= LIST_CAP && <Note className="px-5 py-3">{t('projects.detail.capped', { n: num(LIST_CAP) })}</Note>}
        </Card>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('projects.detail.blockedList')} subtitle={t('projects.detail.blockedListHint')} />
          </div>
          {data.blockedTasks.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('projects.detail.noBlocked')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('projects.detail.task')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th>{t('evidence.col.deadline')}</Th>
                  <Th>{t('projects.detail.lastChange')}</Th>
                  <Th>{t('evidence.item.assignee')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.blockedTasks.map((task) => (
                  <Tr key={task.id}>
                    <Td className="min-w-[14rem]">
                      <TextLink to={sourceHref('TASK', task.id, query) ?? '#'}>{task.title}</TextLink>
                    </Td>
                    <Td>
                      <PriorityBadge priority={task.priority} />
                    </Td>
                    <Td className="whitespace-nowrap">{dates.at(task.dueAt, tz)}</Td>
                    <Td className="whitespace-nowrap">{dates.at(task.updatedAt, tz)}</Td>
                    <Td>
                      <PersonName person={task.assignee} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
          {data.blockedTasks.length >= LIST_CAP && <Note className="px-5 py-3">{t('projects.detail.capped', { n: num(LIST_CAP) })}</Note>}
        </Card>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('projects.detail.ticketsList')} subtitle={t('projects.detail.ticketsListHint')} />
          </div>
          {data.openTickets.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('projects.detail.noTickets')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('projects.detail.ticket')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th>{t('projects.detail.opened')}</Th>
                  <Th>{t('evidence.item.assignee')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.openTickets.map((ticket) => (
                  <Tr key={ticket.id}>
                    <Td className="min-w-[14rem]">
                      <TextLink to={sourceHref('TICKET', ticket.id, query) ?? '#'}>
                        <span dir="ltr" className="me-1.5 font-mono text-caption text-[var(--text-tertiary)]">
                          {ticket.number}
                        </span>
                        {ticket.subject}
                      </TextLink>
                    </Td>
                    <Td>
                      <StatusBadge status={ticket.status} />
                    </Td>
                    <Td>
                      <PriorityBadge priority={ticket.priority} />
                    </Td>
                    <Td className="whitespace-nowrap">{dates.at(ticket.createdAt, tz)}</Td>
                    <Td>
                      <PersonName person={ticket.assignee} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
          {data.openTickets.length >= LIST_CAP && <Note className="px-5 py-3">{t('projects.detail.capped', { n: num(LIST_CAP) })}</Note>}
        </Card>

        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader title={t('projects.detail.contracts')} />
          </div>
          {data.contracts.length === 0 ? (
            <p className="px-5 pb-5 text-body-sm text-muted">{t('projects.detail.noContracts')}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('projects.detail.contract')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('projects.detail.start')}</Th>
                  <Th>{t('projects.detail.end')}</Th>
                  <Th>{t('projects.detail.billing')}</Th>
                  <Th>{t('common.currency')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.contracts.map((c) => (
                  <Tr key={c.id}>
                    <Td className="min-w-[13rem]">
                      <span dir="ltr" className="block font-mono text-caption text-[var(--text-tertiary)]">
                        {c.number}
                      </span>
                      {c.title}
                    </Td>
                    <Td>
                      <StatusBadge status={c.status} />
                    </Td>
                    <Td className="whitespace-nowrap">{dates.day(c.startDate)}</Td>
                    <Td className="whitespace-nowrap">{dates.day(c.endDate)}</Td>
                    <Td>{enumLabel(c.billing, locale)}</Td>
                    <Td>
                      <span dir="ltr" className="font-mono text-caption">
                        {c.currency}
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

/**
 * The last twelve weeks' tracked tasks completed, a week with none as the zero it was. The API sends every week,
 * each starting on the company's own first day of the week, in the company's time zone.
 */
function WeeklyChart({ data }: { data: ProjectDetail }) {
  const { t } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const rows = data.completedByWeek.map((w) => ({ week: w.week, label: dates.shortDay(w.week), done: w.done }));
  const total = rows.reduce((s, r) => s + r.done, 0);
  const name = t('projects.detail.weeklySeries');
  return (
    <Card>
      <CardHeader title={t('projects.detail.weeklyTitle')} subtitle={t('projects.detail.weeklySubtitle')} />
      {total === 0 ? (
        <p className="py-8 text-center text-body-sm text-muted">{t('projects.detail.weeklyNone')}</p>
      ) : (
        <>
          <ChartFrame label={t('projects.detail.weeklyLabel', { n: num(total), weeks: num(rows.length) })} className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" {...AXIS} tickLine={false} minTickGap={8} />
                <YAxis {...AXIS} width={36} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  itemStyle={tooltipItemStyle}
                  labelStyle={tooltipLabelStyle}
                  cursor={{ fill: 'var(--surface-sunken)' }}
                  labelFormatter={(_label, payload) => t('projects.detail.weekOf', { date: dates.day((payload?.[0]?.payload as { week?: string } | undefined)?.week ?? '') })}
                  formatter={(value) => [num(Number(value)), name]}
                />
                <Bar dataKey="done" name={name} fill={SERIES[0]} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
          <TableTwin summary={t('projects.detail.weeklyTable')}>
            <Table>
              <thead>
                <tr>
                  <Th>{t('projects.detail.week')}</Th>
                  <Th align="end">{name}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Tr key={r.week}>
                    <Td>{t('projects.detail.weekOf', { date: dates.day(r.week) })}</Td>
                    <Td align="end" className="tabular">
                      {num(r.done)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableTwin>
        </>
      )}
    </Card>
  );
}
